/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useLayoutEffect, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useRecoilState } from 'recoil';
import { COPY } from 'src/app/constants';
import { checkExerciseAtom } from 'src/app/state/atoms';
import styles from './LanguageSelectorModal.module.scss';
import { t } from 'i18next';
import { getLanguageName, getRegionCode } from 'src/app/utils/language';
import Modal from 'src/app/components/Modal';

interface LanguageSelectorModalProps {
  onSelect: (langCode: string) => void;
  onCancel: () => void;
}

const hasDefaultLangCode = (variants: string[], defaultLang: string) => {
  return variants.some((variant) => variant == defaultLang) ?? false;
};

export const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({
  onSelect,
  onCancel,
}) => {
  const [checkExercise, setCheckExercise] = useRecoilState(checkExerciseAtom);
  const { multilingual_variants } = checkExercise.data;

  const defaultLanauge = useMemo(() => {
    return checkExercise?.data?.default_variant;
  }, [checkExercise]);

  useLayoutEffect(() => {
    if (multilingual_variants?.length) {
      if (!hasDefaultLangCode(multilingual_variants, defaultLanauge)) {
        const listOfLanguages = new Set([
          defaultLanauge,
          ...(checkExercise.data.multilingual_variants ?? []),
        ]);

        setCheckExercise({
          ...checkExercise,
          data: {
            ...checkExercise.data,
            multilingual_variants: Array.from(listOfLanguages),
          },
        });
      }
    } else {
      onCancel();
    }
  }, [checkExercise, multilingual_variants, defaultLanauge, onCancel, setCheckExercise]);

  return (
    <Modal>
      <div className={styles.backdrop} onClick={onCancel}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div>
            {/* HEADER */}
            <div className={styles.header}>
              <h2 className={styles.title}>{t('SELECT_LANGUAGE')}</h2>
              <p className={styles.subtitle}>{COPY.CHOOSE_LANGUAGE}</p>
            </div>

            {/* CONTENT */}
            <div className={styles.content}>
              <div className={styles.languageList}>
                {multilingual_variants?.map((lang, index) => {
                  const regionCode = getRegionCode(lang);

                  return (
                    <button
                      key={lang}
                      className={styles.languageItem}
                      onClick={() => onSelect(lang)}
                      type='button'
                      autoFocus={index === 0}
                    >
                      {regionCode ? (
                        <ReactCountryFlag
                          countryCode={regionCode}
                          svg
                          aria-label={regionCode}
                          className={styles.flag}
                          aria-hidden={true}
                          focusable={false}
                        />
                      ) : (
                        <span className={styles.flagFallback}>{COPY.FALLBACK_FLAG}</span>
                      )}

                      <span className={styles.label}>{getLanguageName(lang, defaultLanauge)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* FOOTER */}
            <div className={styles.actions}>
              <button className={styles.cancelBtn} onClick={onCancel}>
                {COPY.CANCEL}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
